/**
 * Versioned managers — modern implementation (Vite brands, React Compiler).
 *
 * The `useXMgr()` hooks subscribe to the store only to re-render, and return a mutable manager whose
 * getters read the store (`customerMgr.getCurrentCustomer()`). The compiler memoizes those calls on the
 * manager reference, which never changes: the values would stay stale. Here every version (the store
 * values the hook subscribes to) gets its own reference, a proxy of the same manager, so memoized code
 * that depends on it runs again. Same API as `./index.ts`.
 */
import {useMemo} from 'react';

type AnyFunction = (...args: unknown[]) => unknown;

/**
 * A new reference to `manager`: reads and writes go to the manager, methods run with `this` = the manager
 * (private fields, identity in its own code), and keep their identity within a version.
 *
 * @param {object} manager - Mutable manager
 * @returns {object} - Proxy of the manager
 */
export const createVersion = <T extends object>(manager: T): T => {
	const bound = new Map<AnyFunction, AnyFunction>();

	return new Proxy(manager, {
		get(target, property) {
			const value: unknown = Reflect.get(target, property, target);

			if (typeof value !== 'function' || property === 'constructor') {
				return value;
			}

			const method = value as AnyFunction;

			if (!bound.has(method)) {
				bound.set(method, method.bind(target));
			}

			return bound.get(method);
		},
		set: (target, property, value) => Reflect.set(target, property, value, target),
	});
};

/**
 * `manager`, with a new reference whenever one of `versions` changes (compared with `Object.is`).
 *
 * @param {object} manager - Mutable manager (`managerRepository.getXMgr()`)
 * @param {...unknown} versions - Store values the manager reads (the `useSelector` results of the hook)
 * @returns {object} - Proxy of the manager for this version
 */
export const useVersioned = <T extends object>(manager: T, ...versions: unknown[]): T => {
	'use no memo';

	// Same number of versions on every render of a call site.
	// eslint-disable-next-line react-hooks/use-memo
	return useMemo(() => createVersion(manager), [manager, ...versions]);
};
